import React from 'react';
import { API } from '../api';

/*
 * Getting the brief right before the newsletter is written.
 *
 * Writing a whole letter takes a minute and steers itself entirely by the
 * brief, so the brief is worth settling first, while it is a few lines that
 * anybody can read. This sends what the admin has written — with the voice,
 * length and items they have ticked — and gets back a better brief: their
 * intentions in order, the right things featured, and anything a member
 * would need but nobody has said marked [TBC: …] for them to answer.
 *
 * It proposes and never overwrites. The admin reads it, then uses it or keeps
 * their own, and can go round as often as they like before writing the
 * letter. Using it can be undone, back to exactly what they had typed.
 *
 * A gap is never filled in for them: an invented start time would go out to
 * every member as though the club had said it.
 */

function BriefImprover({ request, context, onUse, showToast }) {
  const [proposal, setProposal] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  // What the admin had before they last pressed "Use this brief".
  const [before, setBefore] = React.useState(null);

  const improve = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/newsletters/refine-brief`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request()),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { showToast(data.error || 'It could not work on the brief just now', 'red'); return; }
      setProposal(data);
    } finally { setBusy(false); }
  };

  const use = () => {
    setBefore(context);
    onUse(proposal.brief);
    setProposal(null);
  };

  const undo = () => {
    onUse(before);
    setBefore(null);
  };

  return (
    <div className="notes-check">
      <div className="brief-improver-actions">
        <button type="button" className="w3-button w3-small w3-border" onClick={improve} disabled={busy}>
          {busy
            ? <><i className="fa fa-spinner fa-spin w3-margin-right"></i>Working on it…</>
            : <><i className="fa fa-lightbulb-o w3-margin-right"></i>{context && context.trim() ? 'Improve my brief' : 'Suggest a brief'}</>}
        </button>
        {before !== null && !proposal && (
          <button type="button" className="w3-button w3-small" onClick={undo}>
            <i className="fa fa-undo w3-margin-right"></i>Undo — back to what I wrote
          </button>
        )}
      </div>

      {proposal && (
        <div className="brief-proposal" role="region" aria-label="Suggested brief">
          <span className="gs-section-label">Suggested brief</span>
          <pre className="brief-proposal-text">{proposal.brief}</pre>
          {proposal.questions.length > 0 && (
            <div className="notes-check-gaps">
              <span className="gs-section-label">Worth pinning down</span>
              <ul>
                {proposal.questions.map(q => <li key={q}>{q}</li>)}
              </ul>
              <p>
                These are marked [TBC] in the brief. Put the answers in once you have used it —
                nothing is filled in for you, because an invented time would go out to every
                member as though you had said it.
              </p>
            </div>
          )}
          <div className="brief-improver-actions">
            <button type="button" className="w3-button w3-small gs-btn-primary" onClick={use}>
              <i className="fa fa-check w3-margin-right"></i>Use this brief
            </button>
            <button type="button" className="w3-button w3-small w3-border" onClick={() => setProposal(null)}>
              Keep mine
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BriefImprover;
